import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Snackbar,
  Divider,
  InputAdornment,
  Menu,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  Delete,
  Add,
  Receipt,
  Print,
  Person,
  Description,
  AttachMoney,
  CheckCircle,
  Cancel,
  TrendingDown,
  Category,
  Build,
  ShoppingCart,
  MonetizationOn,
  People,
  EmojiEmotions,
  LocalOffer,
  Business,
  AccountBalance,
  Work,
  School,
  Computer,
  Gavel,
  Home,
  MoreHoriz,
  Edit,
  Paid,
  Pending,
  PictureAsPdf,
  FileDownload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Hotel/api';
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'e-Neurons Luxury Hotel';
const HOTEL_ADDRESS = process.env.REACT_APP_HOTEL_ADDRESS || 'Luxury Hotel Address';
const HOTEL_PHONE = process.env.REACT_APP_HOTEL_PHONE;

const ExpenseManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [statistics, setStatistics] = useState({
    total_expenses: 0,
    total_amount: 0,
    average_amount: 0,
    min_amount: 0,
    max_amount: 0,
    category_breakdown: []
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentStaff, setCurrentStaff] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedExpenseForStatus, setSelectedExpenseForStatus] = useState(null);
  
  // Expense categories
  const expenseCategories = [
    { value: 'maintenance', label: 'Maintenance', icon: <Build />, color: '#ff9800' },
    { value: 'purchases', label: 'Purchases', icon: <ShoppingCart />, color: '#4caf50' },
    { value: 'cash_refund', label: 'Cash Refund', icon: <MonetizationOn />, color: '#f44336' },
    { value: 'staff_expenses', label: 'Staff Expenses', icon: <People />, color: '#9c27b0' },
    { value: 'utilities', label: 'Utilities', icon: <EmojiEmotions />, color: '#2196f3' },
    { value: 'marketing', label: 'Marketing', icon: <LocalOffer />, color: '#e91e63' },
    { value: 'insurance', label: 'Insurance', icon: <Business />, color: '#00bcd4' },
    { value: 'taxes', label: 'Taxes', icon: <AccountBalance />, color: '#607d8b' },
    { value: 'supplies', label: 'Supplies', icon: <Work />, color: '#8bc34a' },
    { value: 'travel', label: 'Travel', icon: <LocalOffer />, color: '#ff5722' },
    { value: 'training', label: 'Training', icon: <School />, color: '#3f51b5' },
    { value: 'software', label: 'Software', icon: <Computer />, color: '#009688' },
    { value: 'professional_fees', label: 'Professional Fees', icon: <Gavel />, color: '#673ab7' },
    { value: 'rent', label: 'Rent', icon: <Home />, color: '#795548' },
    { value: 'other', label: 'Other', icon: <MoreHoriz />, color: '#9e9e9e' },
  ];
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    category: '',
    paymentStatus: '',
    dateRange: 'today',
  });
  
  // Report filter states
  const [reportFilters, setReportFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    category: '',
    paymentStatus: '',
  });
  
  // Expense form states
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    category: '',
    paid_to: '',
    phone: '',
    description: '',
    payment_method: 'Cash',
  });
  
  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Navigation and sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    showSnackbar('You have new notifications!', 'info');
  };

  // Get hotel code and staff details
  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || 'eHT5678042024';
    } catch (error) {
      return 'eHT5678042024';
    }
  };

  const getStaffId = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.staff_id || storedProfile.id || null;
    } catch (error) {
      return null;
    }
  };

  const getStaffName = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return `${storedProfile.first_name || ''} ${storedProfile.last_name || ''}`.trim() || 'Accountant';
    } catch (error) {
      return 'Accountant';
    }
  };

  const getCurrentStaff = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      setCurrentStaff(storedProfile);
      return storedProfile;
    } catch (error) {
      return null;
    }
  };

  // Helper function to extract date
  const extractDate = (datetime) => {
    if (!datetime) return '';
    if (datetime.includes(' ')) return datetime.split(' ')[0];
    if (datetime.includes('T')) return datetime.split('T')[0];
    return datetime;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // API service functions
  const api = {
    getExpenses: async (params = {}) => {
      try {
        const queryParams = { hotel_code: getHotelCode(), ...params };
        Object.keys(queryParams).forEach(key => {
          if (queryParams[key] === undefined || queryParams[key] === '') delete queryParams[key];
        });
        
        const response = await axios.get(`${API_BASE_URL}/hotel/account/expenses.php`, {
          params: queryParams,
          timeout: 10000,
        });
        
        if (response.data && response.data.success) {
          return { success: true, expenses: response.data.expenses || [] };
        }
        return { success: false, expenses: [], error: response.data?.error };
      } catch (error) {
        console.error('Error fetching expenses:', error);
        return { success: false, expenses: [], error: error.message };
      }
    },

    getStatistics: async (params = {}) => {
      try {
        const queryParams = {
          hotel_code: getHotelCode(),
          start_date: params.start_date,
          end_date: params.end_date,
          statistics: 'true'
        };
        
        console.log('Calling statistics API with params:', queryParams);
        
        const response = await axios.get(`${API_BASE_URL}/hotel/account/expenses_statistics.php`, {
          params: queryParams,
          timeout: 10000,
        });
        
        console.log('Statistics API response:', response.data);
        
        if (response.data && response.data.success) {
          return { success: true, statistics: response.data.statistics || {} };
        }
        return { success: false, statistics: {} };
      } catch (error) {
        console.error('Error fetching statistics:', error);
        return { success: false, statistics: {} };
      }
    },
    
    createExpense: async (expenseData) => {
      try {
        const payload = {
          ...expenseData,
          hotel_code: getHotelCode(),
          staff_id: getStaffId(),
          staff_name: getStaffName()
        };
        
        const response = await axios.post(`${API_BASE_URL}/hotel/account/expenses.php`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        return response.data;
      } catch (error) {
        console.error('Error creating expense:', error);
        return { success: false, error: 'Failed to create expense record' };
      }
    },

    updatePaymentStatus: async (id, paymentStatus) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/hotel/account/expenses.php`, {
          id: id,
          hotel_code: getHotelCode(),
          payment_status: paymentStatus
        }, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        return response.data;
      } catch (error) {
        console.error('Error updating payment status:', error);
        return { success: false, error: 'Failed to update payment status' };
      }
    },

    deleteExpense: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/hotel/account/expenses.php`, {
          data: { id: id, hotel_code: getHotelCode() },
          headers: { 'Content-Type': 'application/json' }
        });
        
        return response.data;
      } catch (error) {
        console.error('Error deleting expense:', error);
        return { success: false, error: 'Failed to delete expense record' };
      }
    }
  };

  // Load data function
  const loadData = async () => {
    setLoading(true);
    try {
      const startDate = filters.startDate;
      const endDate = filters.endDate;
      
      console.log('Loading data with filters:', { startDate, endDate, category: filters.category });
      
      const [expensesData, statsData] = await Promise.all([
        api.getExpenses({
          start_date: startDate,
          end_date: endDate,
          category: filters.category || undefined,
          payment_status: filters.paymentStatus || undefined
        }),
        api.getStatistics({
          start_date: startDate,
          end_date: endDate
        })
      ]);

      console.log('Expenses data:', expensesData);
      console.log('Statistics data:', statsData);

      if (expensesData.success) {
        setExpenses(expensesData.expenses);
        setFilteredExpenses(expensesData.expenses);
      } else {
        showSnackbar(expensesData.error || 'Failed to load expenses', 'error');
      }

      if (statsData.success && statsData.statistics) {
        setStatistics(statsData.statistics);
      } else {
        // If statistics API fails, calculate from expenses
        if (expensesData.success && expensesData.expenses.length > 0) {
          const total = expensesData.expenses.reduce((sum, exp) => sum + exp.amount, 0);
          const avg = total / expensesData.expenses.length;
          const max = Math.max(...expensesData.expenses.map(exp => exp.amount));
          
          const categoryMap = {};
          expensesData.expenses.forEach(exp => {
            if (!categoryMap[exp.category]) {
              categoryMap[exp.category] = { count: 0, total_amount: 0 };
            }
            categoryMap[exp.category].count++;
            categoryMap[exp.category].total_amount += exp.amount;
          });
          
          const categoryBreakdown = Object.entries(categoryMap).map(([category, data]) => ({
            category,
            count: data.count,
            total_amount: data.total_amount
          }));
          
          setStatistics({
            total_expenses: expensesData.expenses.length,
            total_amount: total,
            average_amount: avg,
            min_amount: Math.min(...expensesData.expenses.map(exp => exp.amount)),
            max_amount: max,
            category_breakdown: categoryBreakdown
          });
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    if (!expenses || expenses.length === 0) {
      setFilteredExpenses([]);
      return;
    }
    
    let filtered = [...expenses];

    if (filters.startDate) {
      filtered = filtered.filter(expense => extractDate(expense.created_at) >= filters.startDate);
    }

    if (filters.endDate) {
      filtered = filtered.filter(expense => extractDate(expense.created_at) <= filters.endDate);
    }

    if (filters.category) {
      filtered = filtered.filter(expense => expense.category === filters.category);
    }

    if (filters.paymentStatus) {
      filtered = filtered.filter(expense => expense.payment_status === filters.paymentStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(expense =>
        expense.paid_to?.toLowerCase().includes(query) ||
        expense.description?.toLowerCase().includes(query) ||
        expense.expense_number?.toLowerCase().includes(query)
      );
    }

    setFilteredExpenses(filtered);
    setPage(0);
  };

  // Handle date range changes
  const handleDateRangeChange = (range) => {
    const today = new Date();
    
    switch (range) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          startDate: today.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'today'
        }));
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setFilters(prev => ({
          ...prev,
          startDate: yesterday.toISOString().split('T')[0],
          endDate: yesterday.toISOString().split('T')[0],
          dateRange: 'yesterday'
        }));
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        setFilters(prev => ({
          ...prev,
          startDate: startOfWeek.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisWeek'
        }));
        break;
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFilters(prev => ({
          ...prev,
          startDate: startOfMonth.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisMonth'
        }));
        break;
      case 'lastMonth':
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setFilters(prev => ({
          ...prev,
          startDate: firstDayLastMonth.toISOString().split('T')[0],
          endDate: lastDayLastMonth.toISOString().split('T')[0],
          dateRange: 'lastMonth'
        }));
        break;
      case 'custom':
        setFilters(prev => ({ ...prev, dateRange: 'custom' }));
        break;
      default:
        break;
    }
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleDateChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
      dateRange: 'custom'
    }));
  };

  const clearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
      startDate: today,
      endDate: today,
      category: '',
      paymentStatus: '',
      dateRange: 'today',
    });
    setSearchQuery('');
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleNewExpense = () => {
    setExpenseForm({
      amount: '',
      category: '',
      paid_to: '',
      phone: '',
      description: '',
      payment_method: 'Cash',
    });
    setFormErrors({});
    setExpenseDialogOpen(true);
  };

  const handleViewDetails = (expense) => {
    setSelectedExpense(expense);
    setDetailsDialogOpen(true);
  };

  const handlePrintReceipt = (expense) => {
    setReceiptData(expense);
    setPrintDialogOpen(true);
  };

  const handleDeleteExpense = (expense) => {
    setSelectedExpense(expense);
    setDeleteDialogOpen(true);
  };

  const handleStatusMenuOpen = (event, expense) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedExpenseForStatus(expense);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedExpenseForStatus(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedExpenseForStatus) return;
    
    setLoading(true);
    try {
      const result = await api.updatePaymentStatus(selectedExpenseForStatus.id, newStatus);
      if (result.success) {
        showSnackbar(`Payment status updated to ${newStatus}`, 'success');
        await loadData();
      } else {
        showSnackbar(result.error || 'Failed to update status', 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred', 'error');
    } finally {
      setLoading(false);
      handleStatusMenuClose();
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setExpenseForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!expenseForm.amount || expenseForm.amount <= 0) {
      errors.amount = 'Valid amount is required';
    }
    if (!expenseForm.category) {
      errors.category = 'Category is required';
    }
    if (!expenseForm.paid_to || expenseForm.paid_to.trim() === '') {
      errors.paid_to = 'Payee name is required';
    }
    if (!expenseForm.description || expenseForm.description.trim() === '') {
      errors.description = 'Description is required';
    }
    if (!expenseForm.payment_method) {
      errors.payment_method = 'Payment method is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      const firstError = Object.values(formErrors)[0];
      showSnackbar(firstError, 'error');
      return;
    }
    
    setLoading(true);
    try {
      const result = await api.createExpense(expenseForm);
      if (result.success) {
        showSnackbar('Expense recorded successfully!', 'success');
        setExpenseDialogOpen(false);
        await loadData();
        if (result.expense) {
          setReceiptData(result.expense);
          setPrintDialogOpen(true);
        }
      } else {
        showSnackbar(result.error || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showSnackbar('An error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const result = await api.deleteExpense(selectedExpense.id);
      if (result.success) {
        showSnackbar('Expense record deleted successfully!', 'success');
        setDeleteDialogOpen(false);
        await loadData();
      } else {
        showSnackbar(result.error || 'Failed to delete', 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Open report dialog and set initial values from current filters
  const handleOpenReportDialog = () => {
    setReportFilters({
      startDate: filters.startDate,
      endDate: filters.endDate,
      category: filters.category,
      paymentStatus: filters.paymentStatus,
    });
    setReportDialogOpen(true);
  };

  // Handle report filter changes
  const handleReportFilterChange = (field) => (event) => {
    setReportFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  // Generate and print expense report
  const handlePrintReport = () => {
    // Get filtered data based on report filters
    let reportData = [...expenses];
    
    if (reportFilters.startDate) {
      reportData = reportData.filter(expense => extractDate(expense.created_at) >= reportFilters.startDate);
    }
    
    if (reportFilters.endDate) {
      reportData = reportData.filter(expense => extractDate(expense.created_at) <= reportFilters.endDate);
    }
    
    if (reportFilters.category) {
      reportData = reportData.filter(expense => expense.category === reportFilters.category);
    }
    
    if (reportFilters.paymentStatus) {
      reportData = reportData.filter(expense => expense.payment_status === reportFilters.paymentStatus);
    }
    
    // Sort by date descending
    reportData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    // Calculate totals
    const totalAmount = reportData.reduce((sum, exp) => sum + exp.amount, 0);
    const totalCount = reportData.length;
    
    // Create the report HTML content
    const reportContent = `
      <div id="report-content" style="font-family: Arial, sans-serif; padding: 20px; max-width: 1200px; margin: 0 auto;">
        <div style="text-align: center; border-bottom: 3px solid #d32f2f; padding-bottom: 15px; margin-bottom: 20px;">
          <h1 style="color: #d32f2f; margin: 0; font-size: 24px;">${HOTEL_NAME}</h1>
          <p style="margin: 5px 0; color: #666;">${HOTEL_ADDRESS}</p>
          <p style="margin: 5px 0; color: #666;">Tel: ${HOTEL_PHONE}</p>
          <h2 style="color: #333; margin: 15px 0 5px 0;">EXPENSE REPORT</h2>
          <p style="margin: 5px 0; color: #666;">
            Period: ${new Date(reportFilters.startDate).toLocaleDateString()} - ${new Date(reportFilters.endDate).toLocaleDateString()}
          </p>
          ${reportFilters.category ? `<p style="margin: 5px 0; color: #666;">Category: ${expenseCategories.find(c => c.value === reportFilters.category)?.label || reportFilters.category}</p>` : ''}
          ${reportFilters.paymentStatus ? `<p style="margin: 5px 0; color: #666;">Payment Status: ${reportFilters.paymentStatus}</p>` : ''}
        </div>
        
        <div style="display: flex; justify-content: space-around; margin-bottom: 20px; flex-wrap: wrap; gap: 10px;">
          <div style="text-align: center; padding: 15px; background: #f5f5f5; border-radius: 8px; min-width: 150px;">
            <p style="margin: 0; font-size: 14px; color: #666;">Total Expenses</p>
            <p style="margin: 5px 0 0 0; font-size: 20px; font-weight: bold; color: #d32f2f;">${formatCurrency(totalAmount)}</p>
          </div>
          <div style="text-align: center; padding: 15px; background: #f5f5f5; border-radius: 8px; min-width: 150px;">
            <p style="margin: 0; font-size: 14px; color: #666;">Transactions</p>
            <p style="margin: 5px 0 0 0; font-size: 20px; font-weight: bold; color: #1976d2;">${totalCount}</p>
          </div>
          <div style="text-align: center; padding: 15px; background: #f5f5f5; border-radius: 8px; min-width: 150px;">
            <p style="margin: 0; font-size: 14px; color: #666;">Average Expense</p>
            <p style="margin: 5px 0 0 0; font-size: 20px; font-weight: bold; color: #ed6c02;">${totalCount > 0 ? formatCurrency(totalAmount / totalCount) : formatCurrency(0)}</p>
          </div>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #d32f2f; color: white;">
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Receipt #</th>
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Date</th>
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Payee</th>
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Category</th>
              <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Amount</th>
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Payment Method</th>
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.map((expense, index) => `
              <tr style="${index % 2 === 0 ? 'background-color: #f9f9f9;' : ''}">
                <td style="padding: 10px; border: 1px solid #ddd;">${expense.expense_number}</td>
                <td style="padding: 10px; border: 1px solid #ddd;">${new Date(expense.created_at).toLocaleDateString()}</td>
                <td style="padding: 10px; border: 1px solid #ddd;">${expense.paid_to}</td>
                <td style="padding: 10px; border: 1px solid #ddd;">${expenseCategories.find(c => c.value === expense.category)?.label || expense.category}</td>
                <td style="padding: 10px; border: 1px solid #ddd; text-align: right; font-weight: bold;">${formatCurrency(expense.amount)}</td>
                <td style="padding: 10px; border: 1px solid #ddd;">${expense.payment_method}</td>
                <td style="padding: 10px; border: 1px solid #ddd;">
                  <span style="padding: 3px 8px; border-radius: 4px; font-size: 12px; ${expense.payment_status === 'paid' ? 'background: #4caf50; color: white;' : expense.payment_status === 'pending' ? 'background: #ff9800; color: white;' : 'background: #f44336; color: white;'}">
                    ${expense.payment_status}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr style="background-color: #f5f5f5; font-weight: bold;">
              <td colspan="4" style="padding: 10px; border: 1px solid #ddd; text-align: right;">TOTAL</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #d32f2f;">${formatCurrency(totalAmount)}</td>
              <td colspan="2" style="padding: 10px; border: 1px solid #ddd;"></td>
            </tr>
          </tfoot>
        </table>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #ddd;">
          <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 20px;">
            <div>
              <p style="margin: 5px 0; color: #666; font-size: 12px;">Generated By: ${getStaffName()}</p>
              <p style="margin: 5px 0; color: #666; font-size: 12px;">Date Generated: ${new Date().toLocaleString()}</p>
            </div>
            <div style="text-align: right;">
              <p style="margin: 5px 0; color: #666; font-size: 12px;">Hotel Code: ${getHotelCode()}</p>
              <p style="margin: 5px 0; color: #666; font-size: 12px;">Report Type: ${reportFilters.category ? 'Category Report' : 'Full Report'}</p>
            </div>
          </div>
        </div>
        
        <div style="text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #999; font-size: 12px;">
          This is a computer-generated report. No signature required.
        </div>
      </div>
    `;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Expense Report - ${new Date().toLocaleDateString()}</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                #report-content { padding: 20px; }
              }
              body { font-family: Arial, sans-serif; }
            </style>
          </head>
          <body>
            ${reportContent}
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              };
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
      
      // Close the dialog
      setReportDialogOpen(false);
      showSnackbar('Report generated successfully!', 'success');
    } else {
      showSnackbar('Please allow popups for this site', 'error');
    }
  };

  // Export report as CSV
  // Export report as CSV
const handleExportCSV = () => {
  // Get filtered data based on report filters
  let reportData = [...expenses];
  
  if (reportFilters.startDate) {
    reportData = reportData.filter(expense => extractDate(expense.created_at) >= reportFilters.startDate);
  }
  
  if (reportFilters.endDate) {
    reportData = reportData.filter(expense => extractDate(expense.created_at) <= reportFilters.endDate);
  }
  
  if (reportFilters.category) {
    reportData = reportData.filter(expense => expense.category === reportFilters.category);
  }
  
  if (reportFilters.paymentStatus) {
    reportData = reportData.filter(expense => expense.payment_status === reportFilters.paymentStatus);
  }
  
  // Sort by date descending
  reportData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  // Calculate total amount
  const totalAmount = reportData.reduce((sum, exp) => sum + exp.amount, 0);
  
  // Create CSV headers
  const headers = ['Receipt #', 'Date', 'Time', 'Payee', 'Phone', 'Category', 'Amount', 'Payment Method', 'Status', 'Description', 'Recorded By'];
  
  // Create CSV rows
  const rows = reportData.map(expense => [
    expense.expense_number,
    new Date(expense.created_at).toLocaleDateString(),
    new Date(expense.created_at).toLocaleTimeString(),
    expense.paid_to,
    expense.phone || '',
    expenseCategories.find(c => c.value === expense.category)?.label || expense.category,
    expense.amount,
    expense.payment_method,
    expense.payment_status,
    expense.description ? expense.description.replace(/,/g, ';') : '', // Handle commas in description
    expense.staff_name || expense.staff_id || ''
  ]);
  
  // Add summary row
  rows.push([
    '', '', '', '', '', 'TOTAL', totalAmount, '', '', '', ''
  ]);
  
  // Build CSV content
  let csvContent = headers.join(',') + '\n';
  rows.forEach(row => {
    csvContent += row.join(',') + '\n';
  });
  
  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `Expense_Report_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  setReportDialogOpen(false);
  showSnackbar('CSV exported successfully!', 'success');
};

  const handlePrint = () => {
    const printContent = document.getElementById('receipt-content').innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const getCategoryIcon = (categoryValue) => {
    const category = expenseCategories.find(c => c.value === categoryValue);
    return category ? category.icon : <MoreHoriz />;
  };

  const getCategoryColor = (categoryValue) => {
    const category = expenseCategories.find(c => c.value === categoryValue);
    return category ? category.color : '#9e9e9e';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getDateRangeText = () => {
    switch (filters.dateRange) {
      case 'today': return "Today's Expense Records";
      case 'yesterday': return "Yesterday's Expense Records";
      case 'thisWeek': return "This Week's Expense Records";
      case 'thisMonth': return "This Month's Expense Records";
      case 'lastMonth': return "Last Month's Expense Records";
      case 'custom': return `Expense Records from ${filters.startDate} to ${filters.endDate}`;
      default: return "Expense Records";
    }
  };

  // Load initial data
  useEffect(() => {
    getCurrentStaff();
    loadData();
  }, []);

  // Reload data when filters change
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadData();
    }
  }, [filters.startDate, filters.endDate, filters.category, filters.paymentStatus]);

  // Apply filters when data changes
  useEffect(() => {
    applyFilters();
  }, [expenses, filters, searchQuery]);

  // Handle date range changes
  useEffect(() => {
    if (filters.dateRange && filters.dateRange !== 'custom') {
      handleDateRangeChange(filters.dateRange);
    }
  }, [filters.dateRange]);

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">Expense Management</Typography>
                <Typography variant="subtitle1">{getDateRangeText()}</Typography>
                {currentStaff && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    <Person sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Accountant: {currentStaff.first_name} {currentStaff.last_name || ''} ({currentStaff.user_type})
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" startIcon={<Add />} onClick={handleNewExpense} sx={{ backgroundColor: 'white', color: 'error.main' }}>
                  Record Expense
                </Button>
                <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterDialogOpen(true)} sx={{ borderColor: 'white', color: 'white' }}>
                  Filters
                </Button>
                <Button variant="outlined" startIcon={<Print />} onClick={handleOpenReportDialog} sx={{ borderColor: 'white', color: 'white' }}>
                  Print Report
                </Button>
                <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} disabled={loading} sx={{ borderColor: 'white', color: 'white' }}>
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Quick Date Filters */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Quick Date Filters</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Button variant={filters.dateRange === 'today' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'today' }))} size="small">Today</Button>
              <Button variant={filters.dateRange === 'yesterday' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'yesterday' }))} size="small">Yesterday</Button>
              <Button variant={filters.dateRange === 'thisWeek' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'thisWeek' }))} size="small">This Week</Button>
              <Button variant={filters.dateRange === 'thisMonth' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'thisMonth' }))} size="small">This Month</Button>
              <Button variant={filters.dateRange === 'lastMonth' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'lastMonth' }))} size="small">Last Month</Button>
            </Stack>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Total Expenses</Typography>
                      <Typography variant="h4" color="error.main">
                        {formatCurrency(statistics.total_amount || 0)}
                      </Typography>
                    </Box>
                    <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Transactions</Typography>
                      <Typography variant="h4" color="info.main">{statistics.total_expenses || 0}</Typography>
                    </Box>
                    <Receipt sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Average Expense</Typography>
                      <Typography variant="h4" color="warning.main">
                        {formatCurrency(statistics.average_amount || 0)}
                      </Typography>
                    </Box>
                    <AttachMoney sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Highest Expense</Typography>
                      <Typography variant="h4" color="secondary.main">
                        {formatCurrency(statistics.max_amount || 0)}
                      </Typography>
                    </Box>
                    <Category sx={{ fontSize: 40, color: 'secondary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Category Breakdown */}
          {statistics.category_breakdown && statistics.category_breakdown.length > 0 && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Expense by Category</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                {statistics.category_breakdown.map((category) => {
                  const catInfo = expenseCategories.find(c => c.value === category.category);
                  return (
                    <Chip
                      key={category.category}
                      icon={catInfo?.icon}
                      label={`${catInfo?.label || category.category}: ${formatCurrency(category.total_amount)} (${category.count})`}
                      sx={{ backgroundColor: catInfo?.color, color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                    />
                  );
                })}
              </Stack>
            </Paper>
          )}

          {/* Search Bar */}
          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by payee name, description, or receipt number..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment> }}
            />
          </Paper>

          {/* Expenses Table */}
          <Paper elevation={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.error.main }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Receipt #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payee</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 170 }}>Category</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment Method</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredExpenses.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                              No expense records found for the selected period
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredExpenses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((expense) => (
                          <TableRow key={expense.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{expense.expense_number}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{new Date(expense.created_at).toLocaleDateString()}</Typography>
                              <Typography variant="caption" color="textSecondary">{new Date(expense.created_at).toLocaleTimeString()}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{expense.paid_to}</Typography>
                              {expense.phone && <Typography variant="caption" color="textSecondary">{expense.phone}</Typography>}
                            </TableCell>
                            <TableCell sx={{ minWidth: 170 }}>
                              <Chip 
                                icon={getCategoryIcon(expense.category)}
                                label={expenseCategories.find(c => c.value === expense.category)?.label || expense.category}
                                size="small"
                                sx={{ backgroundColor: getCategoryColor(expense.category), color: 'white' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                {formatCurrency(expense.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={expense.payment_method} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="outlined"
                                color={getStatusColor(expense.payment_status)}
                                onClick={(e) => handleStatusMenuOpen(e, expense)}
                                sx={{ textTransform: 'capitalize' }}
                              >
                                {expense.payment_status}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="View Details">
                                  <IconButton size="small" color="primary" onClick={() => handleViewDetails(expense)}>
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Print Receipt">
                                  <IconButton size="small" color="info" onClick={() => handlePrintReceipt(expense)}>
                                    <Print />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredExpenses.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredExpenses.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                  />
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
      >
        <MenuItem onClick={() => handleStatusChange('paid')}>
          <Paid sx={{ mr: 1, color: 'success.main' }} /> Mark as Paid
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('pending')}>
          <Pending sx={{ mr: 1, color: 'warning.main' }} /> Mark as Pending
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('cancelled')}>
          <Cancel sx={{ mr: 1, color: 'error.main' }} /> Mark as Cancelled
        </MenuItem>
      </Menu>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="error" />
            <Typography variant="h6">Filter Expense Records</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="Start Date" 
                type="date" 
                value={filters.startDate} 
                onChange={handleDateChange('startDate')} 
                InputLabelProps={{ shrink: true }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="End Date" 
                type="date" 
                value={filters.endDate} 
                onChange={handleDateChange('endDate')} 
                InputLabelProps={{ shrink: true }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select 
                  value={filters.category} 
                  onChange={handleFilterChange('category')} 
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {expenseCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{category.icon}{category.label}</Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ minWidth: 150 }}>
                <InputLabel>Payment Status</InputLabel>
                <Select value={filters.paymentStatus} onChange={handleFilterChange('paymentStatus')} label="Payment Status">
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => setFilterDialogOpen(false)}>Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Print color="error" />
            <Typography variant="h6">Generate Expense Report</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select the date range and filters for the expense report. The report will include all expenses matching the criteria.
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="Start Date" 
                type="date" 
                value={reportFilters.startDate} 
                onChange={handleReportFilterChange('startDate')} 
                InputLabelProps={{ shrink: true }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="End Date" 
                type="date" 
                value={reportFilters.endDate} 
                onChange={handleReportFilterChange('endDate')} 
                InputLabelProps={{ shrink: true }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select 
                  value={reportFilters.category} 
                  onChange={handleReportFilterChange('category')} 
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {expenseCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{category.icon}{category.label}</Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ minWidth: 150 }}>
                <InputLabel>Payment Status</InputLabel>
                <Select value={reportFilters.paymentStatus} onChange={handleReportFilterChange('paymentStatus')} label="Payment Status">
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Button variant="contained" color="error" startIcon={<Print />} onClick={handlePrintReport}>
            Print Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expense Form Dialog */}
      <Dialog open={expenseDialogOpen} onClose={() => setExpenseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Record New Expense</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Amount *" type="number" value={expenseForm.amount} onChange={handleInputChange('amount')}
                error={!!formErrors.amount} helperText={formErrors.amount}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!formErrors.category} sx={{ minWidth: 150 }}>
                <InputLabel>Category *</InputLabel>
                <Select 
                  value={expenseForm.category} 
                  onChange={handleInputChange('category')} 
                  label="Category *"
                >
                  {expenseCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {category.icon}
                        {category.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Paid To (Payee) *" value={expenseForm.paid_to} onChange={handleInputChange('paid_to')}
                error={!!formErrors.paid_to} helperText={formErrors.paid_to}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Phone Number" value={expenseForm.phone} onChange={handleInputChange('phone')} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Description *" multiline rows={3} value={expenseForm.description} onChange={handleInputChange('description')}
                error={!!formErrors.description} helperText={formErrors.description || "Provide detailed information about this expense"}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!formErrors.payment_method} sx={{ minWidth: 150 }}>
                <InputLabel>Payment Method *</InputLabel>
                <Select value={expenseForm.payment_method} onChange={handleInputChange('payment_method')} label="Payment Method *">
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="POS Terminal">POS Terminal</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Credit Card">Credit Card</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleSubmit} disabled={loading}>Save Expense</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Typography variant="h6">Expense Details</Typography></DialogTitle>
        <DialogContent dividers>
          {selectedExpense && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Receipt Information</Typography>
                <Typography variant="body1"><strong>Receipt #:</strong> {selectedExpense.expense_number}</Typography>
                <Typography variant="body1"><strong>Date:</strong> {new Date(selectedExpense.created_at).toLocaleString()}</Typography>
                <Typography variant="body1"><strong>Category:</strong> <Chip icon={getCategoryIcon(selectedExpense.category)} label={expenseCategories.find(c => c.value === selectedExpense.category)?.label} size="small" sx={{ backgroundColor: getCategoryColor(selectedExpense.category), color: 'white' }} /></Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Payee Information</Typography>
                <Typography variant="body1"><strong>Paid To:</strong> {selectedExpense.paid_to}</Typography>
                {selectedExpense.phone && <Typography variant="body1"><strong>Phone:</strong> {selectedExpense.phone}</Typography>}
                <Typography variant="body1"><strong>Payment Method:</strong> {selectedExpense.payment_method}</Typography>
                <Typography variant="body1"><strong>Amount:</strong> <strong style={{ color: '#f44336' }}>{formatCurrency(selectedExpense.amount)}</strong></Typography>
                <Typography variant="body1"><strong>Status:</strong> <Chip label={selectedExpense.payment_status} size="small" color={getStatusColor(selectedExpense.payment_status)} /></Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}><Typography variant="body2">{selectedExpense.description}</Typography></Paper>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Recorded By</Typography>
                <Typography variant="body1"><strong>Staff:</strong> {selectedExpense.staff_name || selectedExpense.staff_id}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button variant="contained" color="error" startIcon={<Print />} onClick={() => { setDetailsDialogOpen(false); handlePrintReceipt(selectedExpense); }}>Print Receipt</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle><Typography variant="h6">Confirm Delete</Typography></DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this expense record?</Typography>
          {selectedExpense && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Receipt #:</strong> {selectedExpense.expense_number}</Typography>
              <Typography variant="body2"><strong>Amount:</strong> {formatCurrency(selectedExpense.amount)}</Typography>
              <Typography variant="body2"><strong>Paid To:</strong> {selectedExpense.paid_to}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={loading}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Print Dialog */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Typography variant="h6">Print Expense Receipt</Typography></DialogTitle>
        <DialogContent>
          <Box id="receipt-content" sx={{ p: 2, fontFamily: 'monospace' }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6">{HOTEL_NAME}</Typography>
              <Typography variant="caption" display="block">{HOTEL_ADDRESS}</Typography>
              <Typography variant="caption" display="block">Tel: {HOTEL_PHONE}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">EXPENSE RECEIPT</Typography>
              <Divider sx={{ my: 1 }} />
            </Box>
            {receiptData && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2"><strong>Receipt No:</strong> {receiptData.expense_number}</Typography>
                  <Typography variant="body2"><strong>Date:</strong> {new Date(receiptData.created_at).toLocaleString()}</Typography>
                  <Typography variant="body2"><strong>Category:</strong> {expenseCategories.find(c => c.value === receiptData.category)?.label || receiptData.category}</Typography>
                </Box>
                <Divider />
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Paid To:</Typography>
                  <Typography variant="body2"><strong>Name:</strong> {receiptData.paid_to}</Typography>
                  {receiptData.phone && <Typography variant="body2"><strong>Phone:</strong> {receiptData.phone}</Typography>}
                </Box>
                <Divider />
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Payment Details:</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2"><strong>Amount:</strong></Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">{formatCurrency(receiptData.amount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2"><strong>Payment Method:</strong></Typography>
                    <Typography variant="body2">{receiptData.payment_method}</Typography>
                  </Box>
                </Box>
                <Divider />
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Description:</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{receiptData.description}</Typography>
                </Box>
                <Divider />
                <Box sx={{ my: 2 }}>
                  <Typography variant="body2"><strong>Recorded by:</strong> {receiptData.staff_name || receiptData.staff_id}</Typography>
                </Box>
                <Divider />
                <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption">Payee Signature</Typography>
                    <Box sx={{ mt: 4, width: 150, borderBottom: '1px solid black' }} />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption">Accountant Signature</Typography>
                    <Box sx={{ mt: 4, width: 150, borderBottom: '1px solid black' }} />
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="caption" display="block">Thank you for your cooperation!</Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)}>Close</Button>
          <Button variant="contained" color="error" startIcon={<Print />} onClick={handlePrint}>Print Receipt</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpenseManagement;